import { useEffect, useState, useCallback } from "react";
import PageShell from "@/components/PageShell";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { VerifiedBadge, refreshVerified } from "@/components/SupplierName";

export default function Suppliers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => { api.get("/suppliers").then((r) => setItems(r.data)); }, []);
  const { user } = useAuth();
  const toggleVerified = async (s, v) => {
    try { await api.put(`/suppliers/${s.id}/verified`, { verified: v }); load(); refreshVerified(); toast.success(v ? "Badge verified aktif" : "Badge verified dimatikan"); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await api.delete(`/suppliers/${id}`); toast.success("Supplier dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageShell title="Supplier">
      <div className="flex justify-end">
        <SupplierDialog open={open} setOpen={setOpen} edit={edit} setEdit={setEdit} onSaved={load} />
      </div>
      <Card className="shadow-sm"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Kontak</TableHead><TableHead>Alamat</TableHead><TableHead>Verified</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                <TableCell className="font-medium"><button className="text-blue-600 hover:underline" onClick={() => navigate(`/suppliers/${s.id}`)} data-testid={`supplier-open-${s.id}`}><span className="inline-flex items-center gap-1">{s.name}{s.verified && <VerifiedBadge />}</span></button></TableCell>
                <TableCell>{s.contact || "-"}</TableCell>
                <TableCell className="text-sm text-slate-500">{s.address || "-"}</TableCell>
                <TableCell><Switch checked={!!s.verified} disabled={user?.role === "staff"} onCheckedChange={(v) => toggleVerified(s, v)} data-testid={`supplier-verified-${s.id}`} /></TableCell>
                <TableCell className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setEdit(s); setOpen(true); }} data-testid={`supplier-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(s.id)} data-testid={`supplier-delete-${s.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Belum ada supplier.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </PageShell>
  );
}

function SupplierDialog({ open, setOpen, edit, setEdit, onSaved }) {
  const [form, setForm] = useState({ name: "", contact: "", address: "" });
  useEffect(() => { if (edit) setForm({ name: edit.name, contact: edit.contact || "", address: edit.address || "" }); }, [edit]);

  const close = (v) => { setOpen(v); if (!v) { setEdit(null); setForm({ name: "", contact: "", address: "" }); } };
  const submit = async () => {
    if (!form.name) return toast.error("Isi nama supplier");
    try {
      if (edit) await api.put(`/suppliers/${edit.id}`, form);
      else await api.post("/suppliers", form);
      toast.success("Supplier disimpan"); close(false); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild><Button data-testid="add-supplier-button"><Plus className="h-4 w-4 mr-1.5" />Tambah Supplier</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{edit ? "Edit" : "Tambah"} Supplier</DialogTitle><DialogDescription className="sr-only">Form data supplier</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
          <div className="space-y-1.5"><Label>Kontak (opsional)</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="supplier-contact-input" /></div>
          <div className="space-y-1.5"><Label>Alamat (opsional)</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="supplier-address-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => close(false)}>Batal</Button><Button onClick={submit} data-testid="supplier-submit-button">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
